import { useState, useCallback, useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { BillingSidebar, BILLING_SIDEBAR_STORAGE_KEY } from './BillingSidebar';
import { cn } from '@/lib/utils';

const pageConfig: Record<string, string> = {
  '/billing': 'Dashboard',
  '/billing/ai-usage': 'AI Abrechnung',
  '/billing/invoices': 'Rechnungen',
};

export function BillingLayout({ children }: { children: ReactNode }) {
  const location = useLocation();

  const [isSidebarPinned, setIsSidebarPinned] = useState(() => {
    const stored = localStorage.getItem(BILLING_SIDEBAR_STORAGE_KEY);
    return stored === 'true';
  });
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const isSidebarExpanded = isSidebarPinned || isSidebarHovered;

  useEffect(() => {
    localStorage.setItem(BILLING_SIDEBAR_STORAGE_KEY, String(isSidebarPinned));
  }, [isSidebarPinned]);

  // Close mobile nav on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const handleSidebarToggle = useCallback(() => {
    setIsSidebarPinned((prev) => !prev);
  }, []);

  const pageTitle = pageConfig[location.pathname] || 'Billing';

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Desktop Sidebar */}
      <div
        className={cn(
          "hidden md:block fixed inset-y-0 left-0 z-40 transition-all duration-300",
          isSidebarExpanded ? "w-60" : "w-20"
        )}
        onMouseEnter={() => setIsSidebarHovered(true)}
        onMouseLeave={() => setIsSidebarHovered(false)}
      >
        <BillingSidebar
          isExpanded={isSidebarExpanded}
          isPinned={isSidebarPinned}
          onToggle={handleSidebarToggle}
        />
      </div>

      {/* Mobile Sidebar Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 md:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-60 transition-transform duration-300 md:hidden",
          isMobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <BillingSidebar isExpanded isPinned onToggle={() => setIsMobileOpen(false)} />
      </div>

      {/* Main Content */}
      <div className={cn(
        "transition-all duration-300",
        isSidebarExpanded ? "md:pl-60" : "md:pl-20"
      )}>
        {/* Header */}
        <header className="h-16 border-b border-slate-700 bg-slate-800/50 backdrop-blur-sm sticky top-0 z-30">
          <div className="h-full px-4 flex items-center gap-3">
            <button
              onClick={() => setIsMobileOpen(true)}
              className="md:hidden p-2 text-slate-400 hover:text-slate-200 rounded-lg hover:bg-slate-700/50"
              aria-label="Menü öffnen"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-semibold text-slate-100">{pageTitle}</h1>
          </div>
        </header>

        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
